import { Box, Text } from "@simplybusiness/mobius";
import type { ShowcaseProduct, ShowcaseProps } from "../../../pages";
import { PriceBlock } from "../../shared/PriceBlock";

type ProductWithPrice = ShowcaseProduct & {
  estimatedPrice: NonNullable<ShowcaseProduct["estimatedPrice"]>;
};

const hasEstimatedPrice = (
  product: ShowcaseProduct,
): product is ProductWithPrice => {
  const price = product.estimatedPrice;
  return Boolean(
    price &&
    price.premium &&
    price.currencySymbol &&
    price.ratePrefix &&
    price.frequency,
  );
};

export const ShowcasePriceBlock = ({
  showcaseProducts,
}: {
  showcaseProducts: ShowcaseProps["showcaseProducts"];
}) => {
  if (!showcaseProducts.every(hasEstimatedPrice)) {
    return null;
  }

  // TypeScript now knows all products have estimatedPrice
  const productsWithPrices: ProductWithPrice[] = showcaseProducts;
  const firstProductPrice = productsWithPrices[0].estimatedPrice;
  const isMultiProductBundle = productsWithPrices.length > 1;

  const totalPrice = productsWithPrices.reduce(
    (sum, product) => sum + product.estimatedPrice.premium,
    0,
  );

  return (
    <PriceBlock
      priceValue={totalPrice}
      pricePrefix={firstProductPrice.ratePrefix}
      currencySymbol={firstProductPrice.currencySymbol}
      frequency={firstProductPrice.frequency}
      className="mobius-showcase__pricing"
      referenceMark={isMultiProductBundle ? undefined : "*"}
    >
      {isMultiProductBundle && (
        <Box className="mobius-showcase__individual-prices">
          {productsWithPrices.map((product, index) => (
            <Text
              key={`${product.productDisplayName}-${index}`}
              className="mobius-showcase__price"
              elementType="p"
            >
              {product.estimatedPrice.currencySymbol}
              {product.estimatedPrice.premium} for {product.productDisplayName}
              <sup data-testid="price-breakdown-reference-mark">
                {"*".repeat(index + 1)}
              </sup>
            </Text>
          ))}
        </Box>
      )}
    </PriceBlock>
  );
};
